import { ColDef } from 'ag-grid-community';
import React, { useCallback, useRef, useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faBagShopping } from '@fortawesome/pro-light-svg-icons';
import { AgGridTable, AgGridTableRef } from '@shared/components/ui/AgGridTable';
import { usePermissions } from '@shared/hooks/usePermissions';
import { useAgGridColumns } from '../../hooks/useAgGridColumns';
import { Order } from '../../models/Orders';
import { SelectionToolbar } from '../interaction';
import { BulkPreparationModal, BulkPreparationModalRef, ProductsModal, ProductsModalRef } from '../modals';

type BulkActionResult = Promise<boolean> | boolean | void;

interface AgGridTableProps {
  orders: Order[];
  isLoading?: boolean;
  isFetching?: boolean;
  className?: string;
  selectedOrders?: Set<number>;
  onToggleOrderSelection?: (order: Order) => void;
  onSelectAllOrders?: () => void;
  onClearSelection?: () => void;
  refetch?: () => void;
  cancelableCount?: number;
  preparableCount?: number;
  sendableCount?: number;
  uncancelableCount?: number;
  onCancelSelected?: () => BulkActionResult;
  onPrepareSelected?: (preparationType: 'picking_packing' | 'massive_packing') => BulkActionResult;
  onMarkForSendSelected?: () => BulkActionResult;
  onUncancelSelected?: () => BulkActionResult;
  selectedColumns?: string[];
  fetchNextPage?: () => void;
  hasNextPage?: boolean;
  isFetchingNextPage?: boolean;
  onSelectionChanged?: (selectedOrders: Set<number>) => void;
  sorting?: { field: string; direction: 'asc' | 'desc' } | null;
  onSortingChange?: (sorting: { field: string; direction: 'asc' | 'desc' } | null) => void;
}

export const OrdersAgGridTable = React.memo(function OrdersAgGridTable({
  orders,
  isFetching = false,
  selectedOrders,
  onClearSelection,
  refetch,
  cancelableCount = 0,
  preparableCount = 0,
  sendableCount = 0,
  uncancelableCount = 0,
  onCancelSelected,
  onPrepareSelected,
  onMarkForSendSelected,
  onUncancelSelected,
  selectedColumns = [],
  fetchNextPage,
  hasNextPage,
  isFetchingNextPage,
  onSelectionChanged,
  sorting,
  onSortingChange,
}: AgGridTableProps) {
  const agGridRef = useRef<AgGridTableRef>(null);
  const bulkPreparationModalRef = useRef<BulkPreparationModalRef>(null);
  // Estado local para mostrar el toolbar sin latencia
  const [localSelectedCount, setLocalSelectedCount] = useState(0);

  const clearSelectionUI = useCallback(() => {
    agGridRef.current?.deselectAll();
    setLocalSelectedCount(0);
  }, []);

  const handleActionResult = useCallback(
    (result: BulkActionResult | undefined) => {
      if (typeof result === 'boolean') {
        if (result) {
          clearSelectionUI();
        }
        return;
      }

      if (result && typeof (result as Promise<boolean>).then === 'function') {
        (result as Promise<boolean>)
          .then((success) => {
            if (success) {
              clearSelectionUI();
            }
          })
          .catch(() => {
            // noop: el handler superior ya muestra el error si ocurre
          });
      }
    },
    [clearSelectionUI],
  );

  const handleGridReady = useCallback(() => {}, []);

  const handlePrepareSelected = useCallback(() => {
    if (preparableCount > 0) {
      bulkPreparationModalRef.current?.open(preparableCount);
    }
  }, [preparableCount]);

  const handleBulkPreparationConfirm = useCallback(
    (preparationType: 'picking_packing' | 'massive_packing') => {
      const result = onPrepareSelected?.(preparationType);
      handleActionResult(result);
    },
    [onPrepareSelected, handleActionResult],
  );

  const handleCancelSelected = useCallback(() => {
    const result = onCancelSelected?.();
    handleActionResult(result);
  }, [onCancelSelected, handleActionResult]);

  const handleMarkForSendSelected = useCallback(() => {
    const result = onMarkForSendSelected?.();
    handleActionResult(result);
  }, [onMarkForSendSelected, handleActionResult]);

  const handleUncancelSelected = useCallback(() => {
    const result = onUncancelSelected?.();
    handleActionResult(result);
  }, [onUncancelSelected, handleActionResult]);

  const handleCloseSelectionToolbar = useCallback(() => {
    clearSelectionUI();
    onClearSelection?.();
  }, [clearSelectionUI, onClearSelection]);

  // Estado y referencia para ProductsModal
  const productsModalRef = useRef<ProductsModalRef>(null);

  // Handler para abrir el modal
  const handleOpenProductsModal = (order: Order) => {
    const showNotAssigned = order.status?.id === 1 || order.status?.id === 2;
    const payload = {
      orderNumber: order.ref,
      stockMessage:
        'Lorem ipsum dolor sit amet, consectetur adipiscing elit, sed do eiusmod tempor incididunt ut labore et dolore magna aliqua.',
      products: order.lines.map((line) => ({
        name: line.name || line.product?.name || (line.pack ? line.pack.title : ''),
        image: line.product?.image_hash || '',
        badge: line.product?.code || (line.pack ? line.pack.title : ''),
        qty: line.qty,
        price: line.amount,
        subtotal: line.qty * line.amount,
      })),
      subtotal: order.lines.reduce((acc, line) => acc + line.qty * line.amount, 0),
      shipping: order.total_shipping,
      tax: order.total_tax,
      discount: order.total_discount,
      total: order.total,
      showNotAssignedAlert: showNotAssigned,
    } as const;
    productsModalRef.current?.openWithData(payload);
  };

  // Verificar si el usuario es 3PL
  const { getTeamType } = usePermissions();
  const hasTeamData = getTeamType() === 'tenant_3pl';

  const ProductsCell = ({ order, onOpenModal }: { order: Order; onOpenModal: (order: Order) => void }) => {
    const isRed = order.status?.id === 1 || order.status?.id === 2 || order.status?.id === 9;
    const color = isRed ? 'bg-error' : 'bg-success';
    const totalProducts = order.lines?.reduce((acc: number, line: any) => acc + (line?.qty || 0), 0);

    return (
      <span className="text-sm font-medium flex items-center gap-2 relative">
        <button
          type="button"
          className="relative inline-flex items-center gap-2 focus:outline-none hover:opacity-70"
          onClick={() => onOpenModal(order)}
          title="Ver productos"
        >
          <span className="relative inline-block">
            <FontAwesomeIcon icon={faBagShopping} className="w-3 h-3" />
            <span
              className={`absolute -top-1 -right-0 w-1.5 h-1.5 mt-2.5 rounded-full border border-black ${color}`}
            ></span>
          </span>
          {totalProducts}
        </button>
      </span>
    );
  };

  const { filteredColumnDefs } = useAgGridColumns({
    hasTeamData,
    selectedColumns,
    refetch,
  });

  const columnDefsWithRenderers: ColDef[] = filteredColumnDefs.map((col) => {
    if (col.field === 'products') {
      return {
        ...col,
        cellRenderer: (params: any) => <ProductsCell order={params.data} onOpenModal={handleOpenProductsModal} />,
      };
    }
    return col;
  });

  const handleSelectionChanged = useCallback(
    (selectedRows: Order[]) => {
      const selectedIds = new Set(selectedRows.map((order) => order.id));

      setLocalSelectedCount(selectedRows.length);

      if (onSelectionChanged) {
        onSelectionChanged(selectedIds);
      }
    },
    [onSelectionChanged],
  );

  React.useEffect(() => {
    if (selectedOrders && selectedOrders.size === 0) {
      clearSelectionUI();
    }
  }, [selectedOrders, clearSelectionUI]);

  return (
    <div className={`w-full ${isFetching && !isFetchingNextPage ? 'opacity-50 pointer-events-none' : ''}`}>
      <AgGridTable
        ref={agGridRef}
        data={orders}
        columnDefs={columnDefsWithRenderers}
        height="calc(100vh - 200px)"
        enableSelection={true}
        onSelectionChanged={handleSelectionChanged}
        onGridReady={handleGridReady}
        className="rounded-lg shadow-sm"
        fetchNextPage={fetchNextPage}
        hasNextPage={hasNextPage}
        isFetchingNextPage={isFetchingNextPage}
        gridOptions={{}}
        sorting={sorting}
        onSortingChange={onSortingChange}
        rowHeight={50}
      />

      <SelectionToolbar
        selectedCount={localSelectedCount || selectedOrders?.size || 0}
        onClose={handleCloseSelectionToolbar}
        onCancelSelected={handleCancelSelected}
        onPrepareSelected={handlePrepareSelected}
        onMarkForSendSelected={handleMarkForSendSelected}
        onUncancelSelected={handleUncancelSelected}
        cancelableCount={cancelableCount}
        preparableCount={preparableCount}
        sendableCount={sendableCount}
        uncancelableCount={uncancelableCount}
      />

      {/* Modal de productos */}
      <ProductsModal ref={productsModalRef} />

      {/* Modal de preparación masiva */}
      <BulkPreparationModal ref={bulkPreparationModalRef} onConfirm={handleBulkPreparationConfirm} />
    </div>
  );
});
